import type { IOrderAddress, IShippingRequestAddress } from '@rogue/universal'
import { getBuyRequestMeta } from '@/js/helpers/buy-request'
import { checkShippingAddress } from '@/js/helpers/validations/shipping-address'
import type { BuyRequest, BuyRequestMeta } from '@/js/services/types/order'
import type { IRogue } from '@/js/services/RogueTypes'

export type FiveForFiveData = {
  eligibleCount: number,
  validShipping: boolean,
  hasBetterShippingDeal: boolean,
}

function mapChildren(buyRequest: BuyRequest): BuyRequest[] {
  if (buyRequest.children?.length) {
    const buyRequests = buyRequest.children.flatMap(br => JSON.parse(JSON.stringify(mapChildren(br))))
    buyRequests.forEach(br => {
      br.qty = br.qty * buyRequest.qty
    })
    
    return buyRequests
  }

  return [buyRequest]
}

export const fiveForFive = (buyRequests: BuyRequest[], shippingAddress: IShippingRequestAddress | IOrderAddress, rogue: IRogue): FiveForFiveData => {
  const qty = {
    total     : 0,
    eligible  : 0,
    threeFree : 0,
    free      : 0,
  }

  let metas: BuyRequestMeta[] = []

  try {
    metas = buyRequests
      .flatMap(br => mapChildren(br))
      .map(buyRequest => getBuyRequestMeta(buyRequest, rogue))
  } catch (e) {
    rogue.log.info('Error creating buy request metas - not loaded yet?')

    return { eligibleCount: 0, hasBetterShippingDeal: false, validShipping: false }
  }

  // Count the products that are eligible for shipping deals
  metas.forEach(parentMeta => {
    let productMetas = [parentMeta]

    // Loop through bundled products as well
    if (parentMeta.bundleProducts) {
      productMetas = productMetas.concat(parentMeta.bundleProducts)
    }

    // Add qty for products eligible for different deals
    productMetas.forEach(meta => {
      qty.total += parentMeta.qty
      // Add items that are free
      if (meta.flags.freeShip) qty.free += parentMeta.qty
      // Add items that are 3 ships free
      if (meta.flags.free3) qty.threeFree += parentMeta.qty
      // Sort items as either 5 for $5 eligible or not
      // The bundle product should not count toward the 5 for 5 qty since that attribute should be set on the simples
      if (!meta.bundleProducts && meta.flags.fiveForFive) {
        qty.eligible += parentMeta.qty
      }
    })
  })

  // Tests for better shipping deals
  // allItemsFree checks if: totalItems = freeItems + threeShipsFreeItems(if there are 3+)
  const allItemsFree = qty.total === qty.free + (qty.threeFree >= 3 ? qty.threeFree : 0)
  const isFreeCart = metas.some(meta => meta.flags.freeCartShip)
  const total = metas.reduce((prev, meta) => prev + meta.total, 0)

  const validShipping = (!shippingAddress || checkShippingAddress(shippingAddress, rogue))
  const hasBetterShippingDeal = total > 5000 || isFreeCart || allItemsFree

  return {
    eligibleCount: qty.eligible,
    hasBetterShippingDeal,
    validShipping,
  }
}
